import { baseApi } from "./baseApi";

type SubConfigFormType = {
  lease: string;
  price: string;
  deposit: string;
  available_from: string;
  floor?: string;
  occupants: string;
  installments: string;
  price_per: string;
};

type PropertySubConfigType = {
  lease: number;
  price: number;
  deposit: number;
  available_from: string;
  floor?: string;
  occupants: number;
  installments: number;
  price_per: string;
};

export interface PropertyRoomTypesEntry {
  property_id: number;
  room_types: { value: string; label: string }[];
}

export type PropertyReportLogsParams = {
  page: number;
  page_size: number;
  start_time?: string;
  end_time?: string;
  report_type?: string;
  prop_id?: string;
  generated_by?: string;
};

// Floor plans hang off either a configuration (room-type / "global" level) or a
// unit (individual unit / room level). Both share the same three endpoints, only
// the collection base changes.
export type FloorplanLevel = "config" | "unit";

export type FloorplanUploadUrlResponse = {
  upload_url: string;
  key: string;
  headers?: Record<string, string>;
  expires_in?: number;
};

export type FloorplanMedia = {
  id?: number;
  media_id?: number;
  url?: string;
  media_source?: string;
  jpg_file?: string | null;
};

// Floor plans are scoped under the property: /pms/property/{clientCode}/{propId}/
// then either configurations/{id} (config level) or units/{id} (unit level).
const floorplanCollectionUrl = (
  clientCode: string,
  propId: number,
  level: FloorplanLevel,
  id: number
) =>
  `/pms/property/${clientCode}/${propId}/${
    level === "config" ? "configurations" : "units"
  }/${id}/floorplans`;

export const pmsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProperties: builder.mutation({
      query: ({
        clientCode,
        query,
      }: {
        clientCode: string;
        query: string;
      }) => ({
        url: `/pms/properties/${clientCode}?${query}`,
        method: "GET",
      }),
    }),
    searchProperty: builder.mutation({
      query: ({
        clientCode,
        query,
      }: {
        clientCode: string;
        query: string;
      }) => ({
        url: `/pms/properties/${clientCode}/search?name=${query}`,
        method: "GET",
      }),
    }),
    getPropertyByManagerAndName: builder.mutation({
      query: ({
        clientCode,
        propCode,
        page,
        pageSize,
      }: {
        clientCode: string;
        propCode: string;
        page: number;
        pageSize: number;
      }) => ({
        url: `/pms/properties/${clientCode}/${propCode}?config_page=${page}&config_page_size=${pageSize}`,
        method: "GET",
      }),
    }),
    getPropertyLogs: builder.mutation({
      query: (queryString: string) => ({
        url: `/pms/properties/property-logs?${queryString}`,
        method: "GET",
      }),
    }),
    updatePropertyConfiguration: builder.mutation({
      query: ({
        clientCode,
        propId,
        roomName,
        body,
      }: {
        clientCode: string;
        propId: number;
        roomName: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: any;
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration/${roomName}/edit`,
        method: "PATCH",
        body,
      }),
    }),
    createPropertySubconfig: builder.mutation({
      query: ({
        clientCode,
        propId,
        roomName,
        body,
      }: {
        clientCode: string;
        propId: number;
        roomName: string;
        body: SubConfigFormType[];
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration/${roomName}/subconfiguration`,
        method: "POST",
        body: {
          data: body,
        },
      }),
    }),
    updatePropertySubconfig: builder.mutation({
      query: ({
        clientCode,
        propId,
        roomName,
        subConfigId,
        body,
      }: {
        clientCode: string;
        propId: number;
        roomName: string;
        subConfigId: number;
        body: PropertySubConfigType;
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration/${roomName}/subconfiguration/${subConfigId}/edit`,
        method: "PATCH",
        body,
      }),
    }),
    deletePropertySubconfig: builder.mutation({
      query: ({
        clientCode,
        propId,
        roomName,
        subConfigId,
      }: {
        clientCode: string;
        propId: number;
        roomName: string;
        subConfigId: number;
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration/${roomName}/subconfiguration/${subConfigId}/delete`,
        method: "DELETE",
      }),
    }),
    getPropertyFacilities: builder.mutation({
      query: (facilityKind: string) => ({
        url: `/pms/facilities?kind=${facilityKind}`,
        method: "GET",
      }),
    }),
    /* eslint-disable @typescript-eslint/no-explicit-any */
    updatePropertyFacilities: builder.mutation({
      query: (body: any) => ({
        url: `/pms/facilities/add`,
        method: "POST",
        body: {
          data: body,
        },
      }),
    }),
    deletePropertyCommunityFacilities: builder.mutation({
      query: (id: number | string) => ({
        url: `/pms/facilities/delete?propertycommunityfacility_id=${id}`,
        method: "DELETE",
      }),
    }),
    deletePropertyApartmentFacilities: builder.mutation({
      query: (id: number | string) => ({
        url: `/pms/facilities/delete?propertyapartmentfacility_id=${id}`,
        method: "DELETE",
      }),
    }),
    getPropertyTags: builder.mutation({
      query: (tag: string) => ({
        url: `/pms/tags?kind=${tag}`,
        method: "GET",
      }),
    }),
    updatePropertyTags: builder.mutation({
      query: ({
        propId,
        tags,
      }: {
        propId: number;
        tags: { [key: string]: boolean };
      }) => ({
        url: `/pms/tags/add`,
        method: "POST",
        body: {
          prop_id: propId,
          tags,
        },
      }),
    }),
    getRoomConfigs: builder.mutation({
      query: () => ({
        url: `/pms/room-configs`,
        method: "GET",
      }),
    }),
    updateRoomConfigs: builder.mutation({
      query: ({
        clientCode,
        propId,
        roomName,
        body,
      }: {
        clientCode: string;
        propId: number;
        roomName: string;
        body: { [key: string]: boolean | string | number | null };
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration/${roomName}/edit`,
        method: "PATCH",
        body,
      }),
    }),
    createPropertyOffer: builder.mutation({
      query: ({
        clientCode,
        propId,
        body,
      }: {
        clientCode: string;
        propId: number;
        body: { [key: string]: string };
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/offer`,
        method: "POST",
        body,
      }),
    }),
    updatePropertyOffer: builder.mutation({
      query: ({
        clientCode,
        propId,
        offerId,
        body,
      }: {
        clientCode: string;
        propId: number;
        offerId: number;
        body: { [key: string]: string | undefined };
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/offer/${offerId}`,
        method: "PATCH",
        body,
      }),
    }),
    deletePropertyOffer: builder.mutation({
      query: ({
        clientCode,
        propId,
        offerId,
      }: {
        clientCode: string;
        propId: number;
        offerId: number;
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/offer/${offerId}`,
        method: "PATCH",
        body: {
          is_deleted: true,
        },
      }),
    }),
    updatePropertyDetails: builder.mutation({
      query: ({
        clientCode,
        propId,
        body,
      }: {
        clientCode: string;
        propId: number;
        body: { [key: string]: string | number };
      }) => ({
        url: `/pms/properties/${clientCode}/property/${propId}`,
        method: "PATCH",
        body,
      }),
    }),
    createPropertyCategory: builder.mutation({
      query: ({
        clientCode,
        propId,
        body,
      }: {
        clientCode: string;
        propId: number;
        body: any;
      }) => ({
        url: `/pms/properties/${clientCode}/${propId}/configuration`,
        method: "POST",
        body,
      }),
    }),
    getRoomTypes: builder.query({
      query: () => ({
        url: `/pms/bedroom-planner/room-types`,
        method: "GET",
      }),
    }),
    // Room types for multiple properties in one call (comma-separated ids), used to
    // populate the room-type sub-field options in the website-enquiry form builder.
    // Returns [{ property_id, room_types: [{ value, label }] }] (room_types may be empty).
    getPropertyRoomTypes: builder.query<PropertyRoomTypesEntry[], string>({
      query: (propertyIds) => ({
        url: `/pms/property-room-types?property_ids=${propertyIds}`,
        method: "GET",
      }),
    }),
    getFloorPlan: builder.query({
      query: ({ query }: { query: string }) => ({
        url: `/pms/bedroom-planner/rooms${query ? `?${query}` : ""}`,
        method: "GET",
      }),
    }),
    getPropertiesAuto: builder.query({
      query: () => ({
        url: "/pms/properties-list",
        method: "GET",
      }),
    }),
    getPropertyDetails: builder.query({
      query: (propertyId: string | number) => ({
        url: `/pms/property-details/${propertyId}`,
        method: "GET",
      }),
    }),
    getPortfolios: builder.query({
      query: (clientCode: string) => ({
        url: `/pms/investors/${clientCode}`,
        method: "GET",
      }),
    }),
    // ---- floor plans (upload URL -> S3 PUT -> create record; + delete) ----
    getFloorplanUploadUrl: builder.mutation<
      FloorplanUploadUrlResponse,
      {
        clientCode: string;
        propId: number;
        level: FloorplanLevel;
        id: number;
        fileName?: string;
        contentType?: string;
      }
    >({
      query: ({ clientCode, propId, level, id, fileName, contentType }) => ({
        url: `${floorplanCollectionUrl(clientCode, propId, level, id)}/upload-url`,
        method: "POST",
        body: { filename: fileName, content_type: contentType },
      }),
    }),
    createFloorplan: builder.mutation<
      FloorplanMedia,
      {
        clientCode: string;
        propId: number;
        level: FloorplanLevel;
        id: number;
        key: string;
      }
    >({
      // The key from the upload-url step is sent in the body (works for both the
      // config and unit collections).
      query: ({ clientCode, propId, level, id, key }) => ({
        url: floorplanCollectionUrl(clientCode, propId, level, id),
        method: "POST",
        body: { key },
      }),
    }),
    deleteFloorplan: builder.mutation<
      unknown,
      {
        clientCode: string;
        propId: number;
        level: FloorplanLevel;
        id: number;
        mediaId: number | string;
      }
    >({
      query: ({ clientCode, propId, level, id, mediaId }) => ({
        url: `${floorplanCollectionUrl(clientCode, propId, level, id)}/${mediaId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetPropertiesMutation,
  useSearchPropertyMutation,
  useGetPropertyByManagerAndNameMutation,
  useGetPropertyLogsMutation,
  useUpdatePropertyConfigurationMutation,
  useCreatePropertySubconfigMutation,
  useUpdatePropertySubconfigMutation,
  useDeletePropertySubconfigMutation,
  useGetPropertyFacilitiesMutation,
  useUpdatePropertyFacilitiesMutation,
  useDeletePropertyCommunityFacilitiesMutation,
  useDeletePropertyApartmentFacilitiesMutation,
  useGetPropertyTagsMutation,
  useUpdatePropertyTagsMutation,
  useGetRoomConfigsMutation,
  useUpdateRoomConfigsMutation,
  useCreatePropertyOfferMutation,
  useUpdatePropertyOfferMutation,
  useDeletePropertyOfferMutation,
  useUpdatePropertyDetailsMutation,
  useCreatePropertyCategoryMutation,
  useGetRoomTypesQuery,
  useGetPropertyRoomTypesQuery,
  useGetFloorPlanQuery,
  useGetPropertiesAutoQuery,
  useGetPropertyDetailsQuery,
  useGetPortfoliosQuery,
  useGetFloorplanUploadUrlMutation,
  useCreateFloorplanMutation,
  useDeleteFloorplanMutation,
} = pmsApi;
